<template>
  <div class="date-picker-root" :style="getRootStyle">
    <div class="form-label label-text" v-if="label">
      {{ $t(label) }}
      <span v-if="is_required" class="req-sign">*</span>
    </div>
    <validation-provider
      :name="name"
      :rules="rules"
      v-slot="{ errors }"
      :id="id"
    >
      <input type="text" v-model="inputfield" class="d-none" />
      <div
        class="form-control disabled"
        :style="cus_style"
        v-if="disabled || readOnly"
      >
        <span style="font-size: 14px">
          <span>
            <span v-if="dateRange.startDate">
              {{
                dateRange.startDate
                  | moment(timePicker ? "DD-MM-YYYY, H:mm" : "DD-MM-YYYY")
              }}
            </span>
            <span v-else-if="placeholder" style="color: var(--color-83)">
              {{ $t(placeholder) }}
            </span>
            <span v-else-if="label" style="color: var(--color-83)">
              {{ $t(label) }}
            </span>
            <span v-else>{{
              timePicker ? "DD-MM-YYYY, H:mm" : "DD-MM-YYYY"
            }}</span>
          </span>
        </span>
      </div>
      <date-range-picker
        v-else
        :class="[
          getPositionCalender,
          {
            'invalid-range':
              is_invalid || (errors && errors.length) ? true : false,
            disabled: disabled,
          },
        ]"
        ref="picker"
        :locale-data="locale_data"
        :opens="isLangRtl ? 'left' : 'right'"
        :minDate="minDate"
        :maxDate="maxDate === false ? null : maxDate"
        :singleDatePicker="true"
        :ranges="false"
        :timePicker="timePicker"
        :timePicker24Hour="true"
        :showWeekNumbers="false"
        :showDropdowns="false"
        :autoApply="false"
        v-model="dateRange"
        @update="updateValues"
        :disabled="disabled ? true : false"
      >
        <template v-slot:input="picker">
          <span style="font-size: 14px">
            <span>
              <span v-if="picker.startDate">
                {{
                  picker.startDate
                    | moment(timePicker ? "DD-MM-YYYY, H:mm" : "DD-MM-YYYY")
                }}
              </span>
              <span v-else-if="placeholder" style="color: var(--color-83)">
                {{ $t(placeholder) }}
              </span>
              <span v-else-if="label" style="color: var(--color-83)">
                {{ $t(label) }}
              </span>
              <span v-else>{{
                timePicker ? "DD-MM-YYYY, H:mm" : "DD-MM-YYYY"
              }}</span>
            </span>
          </span>
        </template>
      </date-range-picker>

      <div v-if="helper_text" class="helper-text" :style="helper_text_style">
        {{ $t(helper_text) }}
      </div>
      <div
        class="error-text"
        :style="helper_text_style"
        v-if="errors && errors.length"
      >
        {{ errors[0] }}
      </div>
      <slot name="attachment"></slot>
    </validation-provider>
  </div>
</template>

<script>
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export default {
  emits: ["change-value", "change"],
  props: [
    "timePicker",
    "label",
    "placeholder",
    "is_invalid",
    "helper_text",
    "helper_text_style",
    "date_field",
    "time_field",
    "value",
    "cus_style",
    "remove_default_margin",
    "readOnly",
    "disabled",
    "cus_ref",
    "is_required",
    "options",
    "rules",
    "name",
    "id",
    "not_future_date",
    "minDate",
    "maxDate",
    "position",
  ],
  components: { DateRangePicker },
  data() {
    return {
      inputfield: null,
      dateRange: {
        startDate: null,
        endDate: null,
      },
      locale_data: {
        firstDay: 0,
        direction: this.isLangRtl ? "rtl" : "ltr",
        separator: " - ",
        applyLabel: this.$t("save"),
        cancelLabel: this.$t("cancel"),
        daysOfWeek: [
          this.$t("Sun"),
          this.$t("Mon"),
          this.$t("Tue"),
          this.$t("Wed"),
          this.$t("Thu"),
          this.$t("Fri"),
          this.$t("Sat"),
        ],
        monthNames: [
          this.$t("January"),
          this.$t("February"),
          this.$t("March"),
          this.$t("April"),
          this.$t("May"),
          this.$t("June"),
          this.$t("July"),
          this.$t("August"),
          this.$t("September"),
          this.$t("October"),
          this.$t("November"),
          this.$t("December"),
        ],
      },
    };
  },
  computed: {
    getRootStyle() {
      return `margin-bottom: ${
        this.remove_default_margin ? "0px" : "20px"
      };position:relative;`;
    },
    getPositionCalender() {
      if (!this.position) return;
      return this.position === "left" ? "calendar-left" : "calendar-right";
    },
  },
  methods: {
    updateValues(val) {
      let date_value, time_value;
      if (val?.startDate) {
        let temp = val.startDate;
        time_value = moment(temp).locale("en").format("HH:mm:ss");
        date_value = new Date(new Date(temp).setHours(0, 0, 0, 0));
      }
      this.$emit(
        "change-value",
        date_value,
        this.date_field,
        time_value,
        this.time_field
      );
      this.$emit("change", date_value, time_value);
    },
    setValue(value) {
      let startDate = null;
      if (value[this.date_field]) startDate = new Date(value[this.date_field]);
      if (value[this.time_field])
        startDate = startDate?.setHours(
          value[this.time_field].substring(0, 2),
          value[this.time_field].substring(3, 5),
          value[this.time_field].substring(6, 8)
        );
      this.dateRange = { startDate: startDate, endDate: startDate };
    },
  },
  watch: {
    dateRange(val) {
      this.inputfield = val?.startDate;
    },
    value: {
      deep: true,
      handler(val) {
        this.setValue(val);
      },
    },
  },
  mounted() {
    this.setValue(this.value);
  },
  i18n: {
    messages: {
      en: require("@/services/i18n/locales/en/reservationOperations.json"),
      ar: require("@/services/i18n/locales/ar/reservationOperations.json"),
    },
  },
};
</script>

<style lang="scss" scoped>
.label-text {
  font-size: 14px;
  color: var(--color-61);
  margin-bottom: 5px;
  font-weight: 700;
}
</style>

<style lang="scss">
.vue-daterange-picker {
  width: 100%;
}
.reportrange-text {
  width: 100%;
  height: 40px;
  font-size: 14px;
  padding: 10px;
  background: var(--color-01);
  color: var(--color-61);
  border: 1px solid;
  border-radius: 6px;
  border-color: var(--color-64);
  box-shadow: 0px 1px 2px var(--color-37);
  font-weight: 400;

  &:focus-visible {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:hover {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:focus {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:disabled {
    background-color: var(--color-67);
  }
}
.disabled .reportrange-text {
  border-color: var(--color-67) !important;
  background-color: var(--color-67) !important;
}
.invalid-range .reportrange-text {
  border-color: var(--color-47) !important;
  background-color: var(--color-01) !important;
}
.daterangepicker td.active,
.daterangepicker td.active:hover {
  background-color: var(--color-23) !important;
  color: var(--color-61) !important;
}
.daterangepicker td.in-range {
  background-color: var(--color-64);
  color: var(--color-61);
}
.calendar-left .daterangepicker {
  left: auto !important;
  right: 0 !important;
  
  &::after {
    right: 10px;
    left: auto !important;
  }
  &::before {
    right: 9px;
    left: auto !important;
  }
}
.calendar-right .daterangepicker {
  left: 0 !important;
  right: auto !important;
  
  &::after {
    left: 10px !important;
    right: auto !important;
  }
  &::before {
    left: 9px !important;
    right: auto !important;
  }
}
</style>
